import { getComponentData } from '@backstage/core-plugin-api';
import { isValidElement, Children } from 'react';
import { createCollector } from '../extensions/traversal.esm.js';
import { FeatureFlagged } from './FeatureFlagged.esm.js';

const MATCH_ALL_ROUTE = {
  caseSensitive: false,
  path: "*",
  element: "match-all",
  // These elements aren't used, so we add in a bit of debug information
  routeRefs: /* @__PURE__ */ new Set(),
  plugins: /* @__PURE__ */ new Set()
};
function stringifyNode(node) {
  const anyNode = node;
  if (anyNode?.type) {
    return anyNode.type.displayName ?? anyNode.type.name ?? String(anyNode.type);
  }
  return String(anyNode);
}
const pluginSet = (plugin) => {
  const set = /* @__PURE__ */ new Set();
  if (plugin) {
    set.add(plugin);
  }
  return set;
};
function collectSubTree(node, entries = new Array()) {
  Children.forEach(node, (element) => {
    if (!isValidElement(element)) {
      return;
    }
    const routeRef = getComponentData(element, "core.mountPoint");
    if (routeRef) {
      const plugin = getComponentData(element, "core.plugin");
      entries.push({ routeRef, plugin });
    }
    collectSubTree(element.props.children, entries);
  });
  return entries;
}
const routingV2Collector = createCollector(
  () => ({
    paths: /* @__PURE__ */ new Map(),
    parents: /* @__PURE__ */ new Map(),
    objects: new Array()
  }),
  (acc, node, parent, ctx) => {
    if (ctx?.isElementAncestor) {
      return ctx;
    }
    if (parent?.props.element === node) {
      return { ...ctx, isElementAncestor: true };
    }
    const pathProp = node.props?.path;
    const mountPoint = getComponentData(node, "core.mountPoint");
    if (mountPoint && pathProp) {
      throw new Error(
        `Path property may not be set directly on a routable extension "${stringifyNode(
          node
        )}"`
      );
    }
    const parentChildren = ctx?.obj?.children ?? acc.objects;
    if (pathProp !== void 0) {
      if (typeof pathProp !== "string") {
        throw new Error(
          `Element path must be a string at "${stringifyNode(node)}"`
        );
      }
      const path = pathProp.startsWith("/") ? pathProp.slice(1) : pathProp;
      const elementProp = node.props.element;
      if (getComponentData(node, "core.gatherMountPoints")) {
        if (elementProp) {
          throw new Error(
            `Mount point gatherers may not have an element prop "${stringifyNode(
              node
            )}"`
          );
        }
        const newObj = {
          path,
          element: "gathered",
          routeRefs: /* @__PURE__ */ new Set(),
          caseSensitive: Boolean(node.props?.caseSensitive),
          children: [MATCH_ALL_ROUTE],
          plugins: /* @__PURE__ */ new Set()
        };
        parentChildren.push(newObj);
        return {
          obj: newObj,
          gatherPath: path,
          routeRef: ctx?.routeRef,
          gatherRouteRef: ctx?.routeRef
        };
      }
      if (elementProp) {
        const [extension, ...others] = collectSubTree(elementProp);
        if (others.length > 0) {
          throw new Error(
            `Route element with path "${pathProp}" may not contain multiple routable extensions`
          );
        }
        if (!extension) {
          return ctx;
        }
        const { routeRef, plugin } = extension;
        const newObj = {
          path,
          element: "mounted",
          routeRefs: /* @__PURE__ */ new Set([routeRef]),
          caseSensitive: Boolean(node.props?.caseSensitive),
          children: [MATCH_ALL_ROUTE],
          plugins: pluginSet(plugin)
        };
        parentChildren.push(newObj);
        acc.paths.set(routeRef, path);
        acc.parents.set(routeRef, ctx?.routeRef);
        return {
          obj: newObj,
          routeRef: routeRef ?? ctx?.routeRef,
          gatherPath: path,
          gatherRouteRef: ctx?.gatherRouteRef
        };
      }
    }
    if (mountPoint) {
      if (ctx?.gatherPath === void 0) {
        throw new Error(
          `Routable extension "${stringifyNode(
            node
          )}" with mount point "${mountPoint}" must be assigned a path`
        );
      }
      ctx?.obj?.routeRefs.add(mountPoint);
      const mountPointPlugin = getComponentData(
        node,
        "core.plugin"
      );
      if (mountPointPlugin) {
        ctx?.obj?.plugins.add(mountPointPlugin);
      }
      acc.paths.set(mountPoint, ctx.gatherPath);
      acc.parents.set(mountPoint, ctx?.gatherRouteRef);
      return {
        ...ctx,
        routeRef: mountPoint
      };
    }
    return ctx;
  }
);
const routingV1Collector = createCollector(
  () => ({
    paths: /* @__PURE__ */ new Map(),
    parents: /* @__PURE__ */ new Map(),
    objects: new Array()
  }),
  (acc, node, parent, ctx) => {
    if (parent?.props.element === node) {
      return ctx;
    }
    let currentObj = ctx?.obj;
    let currentParentRouteRef = ctx?.routeRef;
    let sticky = ctx?.sticky;
    const path = node.props?.path;
    const parentChildren = currentObj?.children ?? acc.objects;
    const caseSensitive = Boolean(node.props?.caseSensitive);
    let currentCtxPath = ctx?.path;
    if (getComponentData(node, "core.gatherMountPoints")) {
      if (!path) {
        throw new Error("Mount point gatherer must have a path");
      }
      currentCtxPath = path;
    }
    const element = node.props?.element;
    let routeRef = getComponentData(node, "core.mountPoint");
    if (!routeRef && isValidElement(element)) {
      routeRef = getComponentData(element, "core.mountPoint");
    }
    if (routeRef) {
      let routePath = path;
      if (currentCtxPath) {
        if (routePath) {
          currentCtxPath = void 0;
        } else {
          routePath = currentCtxPath;
        }
      }
      if (!routePath) {
        throw new Error("Mounted routable extension must have a path");
      }
      acc.paths.set(routeRef, routePath);
      if (currentParentRouteRef && sticky) {
        acc.parents.set(routeRef, currentParentRouteRef);
        if (path) {
          currentParentRouteRef = routeRef;
          sticky = false;
        }
      } else {
        acc.parents.set(routeRef, currentParentRouteRef);
        currentParentRouteRef = routeRef;
      }
      if (path) {
        currentObj = {
          caseSensitive,
          path,
          element: "mounted",
          routeRefs: /* @__PURE__ */ new Set([routeRef]),
          children: [MATCH_ALL_ROUTE],
          plugins: pluginSet(
            getComponentData(
              node.props.element,
              "core.plugin"
            )
          )
        };
        parentChildren.push(currentObj);
      } else {
        currentObj?.routeRefs.add(routeRef);
      }
    }
    if (getComponentData(node, "core.gatherMountPoints")) {
      sticky = true;
    }
    const isGatherer = getComponentData(
      node,
      "core.gatherMountPoints"
    );
    if (isGatherer) {
      if (!path) {
        throw new Error("Mount point gatherer must have a path");
      }
      if (!routeRef) {
        currentObj = {
          caseSensitive,
          path,
          element: "gathered",
          routeRefs: /* @__PURE__ */ new Set(),
          children: [MATCH_ALL_ROUTE],
          plugins: ctx?.obj?.plugins || /* @__PURE__ */ new Set()
        };
        parentChildren.push(currentObj);
      }
    }
    return {
      obj: currentObj,
      path: currentCtxPath,
      routeRef: currentParentRouteRef,
      sticky
    };
  }
);
const featureFlagCollector = createCollector(
  () => /* @__PURE__ */ new Set(),
  (acc, node) => {
    if (node.type === FeatureFlagged) {
      const props = node.props;
      acc.add("with" in props ? props.with : props.without);
    }
  }
);

export { MATCH_ALL_ROUTE, featureFlagCollector, routingV1Collector, routingV2Collector };
//# sourceMappingURL=collectors.esm.js.map
